import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Select, pubnub } from 'wwtc-sdk';
import './login.css';
import './common/button.css';
import { getGatewayLanguagePreference } from './languagePreference';
import { getGatewayNavigationPath } from './invitationLink';
import { createGuestWaitingRoomController } from '../waitingRoom';

// Get value from query URL
export const getValueFromQuery = (value) => {
    const plainUrl = window.location.href;
    const objUrl = new URL(plainUrl);

    return objUrl.searchParams.get(value);
};

function Invitation({ languages }) {
    const history = useHistory();

    const room = getValueFromQuery('room');

    const [form, setForm] = useState({
        language: null,
        displayName: null,
        gender: null,
    });
    const [info, setInfo] = useState({
        loading: false,
        error: null,
        languages: [],
    });
    const [errors, setErrors] = useState([]);
    const [isModeratorPresent, setIsModeratorPresent] = useState(false);
    const [waitingRoom, setWaitingRoom] = useState(false);
    const [waitingRoomError, setWaitingRoomError] = useState(null);
    const waitingRoomControllerRef = useRef(null);
    const joinRoomRef = useRef(null);
    const myPubnubId = pubnub.userId;
    const activeWaitingRoom = window.activeWaitingRoom;
    const allowJoinWithoutModerator = window.allowJoinWithoutModerator;

    useEffect(() => {
        joinRoomRef.current = joinRoom;
    });

    useEffect(() => {
        const kickoutListener = {
            signal: function (s) {
                if (s.message.search('kickout') >= 0) {
                    const participantId = s.message.split('kickout_participant-')[1];
                    if (participantId === myPubnubId) {
                        window.location.replace(window.location.origin);
                    }
                }
            },
        };
        const controller = createGuestWaitingRoomController({
            client: pubnub,
            room,
            participantId: myPubnubId,
            clientType: 'now-translate',
            onModeratorPresence: setIsModeratorPresent,
            onAdmit: () => joinRoomRef.current(),
            onDeny: () => {
                setWaitingRoom(false);
                setWaitingRoomError(
                    'The moderator denied your request. You can update your details and try again.',
                );
            },
        });

        pubnub.addListener(kickoutListener);
        waitingRoomControllerRef.current = controller;
        controller.connect();

        return () => {
            controller.disconnect();
            waitingRoomControllerRef.current = null;
            pubnub.removeListener(kickoutListener);
        };
    }, [myPubnubId, room]);

    useEffect(() => {
        const preferredLanguage = getGatewayLanguagePreference(languages);
        if (preferredLanguage) {
            setForm((currentForm) =>
                currentForm.language ? currentForm : { ...currentForm, language: preferredLanguage },
            );
        }
    }, [languages]);

    const handleLanguage = (language) => {
        setForm({ ...form, language });
    };

    const handleGender = (gender) => {
        setForm({ ...form, gender });
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (validateLogin()) {
            if (activeWaitingRoom) {
                setWaitingRoomError(null);
                setWaitingRoom(true);
                try {
                    await waitingRoomControllerRef.current?.request(form.displayName);
                } catch (error) {
                    setWaitingRoom(false);
                    setWaitingRoomError('Unable to contact the moderator. Please try again.');
                }
            } else {
                joinRoom();
            }
        } else {
            setInfo({
                ...info,
                error: 'Please fill inputs to continue',
            });
        }
    };

    const joinRoom = () => {
        const { language, displayName, gender } = form;
        setInfo((currentInfo) => ({ ...currentInfo, loading: true }));
        history.push(getGatewayNavigationPath('/app'), {
            userName: displayName,
            room,
            language: language.value,
            gender,
            userInvite: true,
            apiToken: window.apiToken,
            languages,
        });
    };

    const validateLogin = () => {
        let valid = true;
        let errors = [];
        Object.keys(form).forEach((item) => {
            if (!form[item] || form[item] === '') {
                valid = false;
                errors.push(item);
            }
        });
        setErrors(errors);
        return valid;
    };

    const errorDisplayName = errors.find((item) => item === 'displayName');
    const errorGender = errors.find((item) => item === 'gender');

    return (
        <div className="wrapper-login">
            <div className="wrapper-content">
                <div className="wrapper-login-modal">
                    <div className="wrapper-logo">
                        <img src={`${window.logoLogin}`} alt="WWTC" />
                    </div>
                    <form className="form-modal" onSubmit={handleLogin}>
                        <div className="title-welcome">
                            <h2>Join Invitation</h2>
                        </div>
                        {!waitingRoom ? (
                            <div className="wrapper-login-form">
                                <div className="login-inputs">
                                    <Select
                                        placeholder="Select language"
                                        handleChange={handleLanguage}
                                        data={languages}
                                        value={form.language || ''}
                                    />
                                </div>
                                <div className="login-inputs">
                                    <input
                                        aria-label="Name"
                                        className={`Input ${errorDisplayName ? 'Input-error' : ''}`}
                                        type="text"
                                        placeholder="Name"
                                        value={form.displayName || ''}
                                        onChange={(e) =>
                                            setForm({ ...form, displayName: e.target.value })
                                        }
                                    />
                                </div>
                                <div
                                    className="login-inputs"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-evenly',
                                        alignItems: 'center',
                                    }}
                                >
                                    <button
                                        type="button"
                                        className={`Button Button-small Button-gender ${
                                            form.gender === 'male' ? 'Button-gender-active' : ''
                                        }`}
                                        onClick={() => handleGender('male')}
                                    >
                                        <span>Masculine</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`Button Button-small Button-gender ${
                                            form.gender === 'female' ? 'Button-gender-active' : ''
                                        }`}
                                        onClick={() => handleGender('female')}
                                    >
                                        <span>Feminine</span>
                                    </button>
                                </div>
                                <div style={{ height: 16 }}>
                                    {errorGender ? (
                                        <p
                                            style={{
                                                margin: 0,
                                                textAlign: 'center',
                                                fontSize: 14,
                                                color: 'lightcoral',
                                            }}
                                        >
                                            Select gender to continue
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <div style={{ marginTop: 36, marginBottom: 36 }}>
                                <p style={{ textAlign: 'center' }}>
                                    Please wait. The moderator will let you in shortly.
                                </p>
                            </div>
                        )}
                        {!waitingRoom && (
                            <div className="wrapper-button-actions">
                                <button
                                    onClick={handleLogin}
                                    type="submit"
                                    className={`Button ${
                                        isModeratorPresent || allowJoinWithoutModerator
                                            ? 'Button-primary'
                                            : 'Button-gray'
                                    } Button-block`}
                                    disabled={!isModeratorPresent && !allowJoinWithoutModerator}
                                >
                                    {info.loading ? (
                                        <img
                                            src="https://sdkresources.s3.amazonaws.com/assets/oval.svg"
                                            style={{ width: 14, height: 14 }}
                                            alt="loading"
                                        />
                                    ) : (
                                        'connect'
                                    )}
                                </button>
                            </div>
                        )}
                        <div className="error-message">
                            {info.error ? <p>{info.error}</p> : null}
                        </div>
                        {waitingRoomError && !waitingRoom && (
                            <div className="error-message" role="alert">
                                <p>{waitingRoomError}</p>
                            </div>
                        )}
                        {!waitingRoom && !isModeratorPresent && !allowJoinWithoutModerator && (
                            <div className="error-message">
                                <p style={{ color: 'red' }}>
                                    Waiting for Moderator to Begin Meeting
                                </p>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Invitation;
